import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Monitor, Users, Zap, Download } from 'lucide-react';
import { downloadQR } from '../utils/qrDownload';
import { usePublicBase } from '../utils/shortUrl';
import CoordinatorQrPanel from './CoordinatorQrPanel';
import { sportsApi } from '../SportsAuthContext';

const RED = '#E63946';

function Panel({ icon: Icon, color, title, subtitle, children }) {
  return (
    <div className="rounded-xl p-5" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" style={{ color }} />
        <p className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif', color }}>{title}</p>
      </div>
      <p className="text-xs mb-4" style={{ color: '#555' }}>{subtitle}</p>
      {children}
    </div>
  );
}

function QrBlock({ id, url, label, sublabel, size = 170, color, filename, testid }) {
  return (
    <div className="flex flex-col items-center">
      <div className="p-4 rounded-xl mb-3" style={{ background: '#fff' }}>
        <QRCodeSVG id={id} value={url} size={size} level="H" includeMargin data-label={label} data-sublabel={sublabel} />
      </div>
      {sublabel && <p className="text-xs font-bold mb-3" style={{ color: '#A3A3A3' }}>{sublabel}</p>}
      <button data-testid={testid} onClick={() => downloadQR(id, filename)}
        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold"
        style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
        <Download className="h-3.5 w-3.5" /> Download Poster QR
      </button>
    </div>
  );
}

// QR hub for an Open Play session — parity with the tournament QRCodesHub.
export default function OpenPlayQRHub({ session, sessionId, slug, courtQueues }) {
  const origin = window.location.origin;
  const base = usePublicBase(slug);
  const liveUrl = session.short_code && base
    ? `${base}/p/${session.short_code}/live`
    : `${origin}/book/${slug}/open-play/${sessionId}`;
  const regUrl = session.short_code && base
    ? `${base}/p/${session.short_code}`
    : `${origin}/book/${slug}/open-play/${sessionId}/register`;
  const safeName = (session.name || 'open-play').replace(/\s+/g, '-');

  // One PERMANENT scorer QR per court: it opens whatever match is live on that
  // court and moves on by itself (the old poster encoded one match's token and
  // went stale after a single game).
  const [courtTokens, setCourtTokens] = useState(null);
  useEffect(() => {
    if (session.status === 'ended') return;
    sportsApi.get(`/sports/open-play/${sessionId}/court-scorer-tokens`)
      .then(r => setCourtTokens(r.data || []))
      .catch(() => setCourtTokens([]));
  }, [sessionId, session.status]);
  const liveOn = Object.fromEntries((courtQueues || []).map(c => {
    const s = (c.slots || []).find(x => x.status === 'active');
    return [c.court_id, s];
  }));

  return (
    <div data-testid="op-qr-hub" className="space-y-4">
      {session.status !== 'ended' && (
        <Panel icon={Users} color="#22C55E" title="Registration QR"
          subtitle="Players scan to register for this open play session.">
          <QrBlock id="op-qr-registration" url={regUrl} label={session.name}
            sublabel="Scan to register & join" color="#22C55E"
            filename={`register-${safeName}.png`} testid="op-download-reg-qr" size={160} />
        </Panel>
      )}

      <CoordinatorQrPanel apiBase={`/sports/open-play/${sessionId}`} eventName={session.name} />

      <Panel icon={Monitor} color="#FFB300" title="Live Queue QR — Post at Venue"
        subtitle="Players & spectators scan to follow the live rotation queue, matchups and leaderboard.">
        <QrBlock id="op-qr-live" url={liveUrl} label={session.name}
          sublabel="Scan for LIVE queue & leaderboard" color="#FFB300"
          filename={`live-${safeName}.png`} testid="op-download-live-qr" size={180} />
      </Panel>

      <Panel icon={Zap} color={RED} title="Scorer QR Codes — Print & Place on Courts"
        subtitle="One per court — print once and leave it on the court. It always opens the match being played there.">
        {session.status === 'ended' ? (
          <p className="text-sm text-center py-6" style={{ color: '#444' }}>This session has ended.</p>
        ) : courtTokens === null ? (
          <p className="text-sm text-center py-6" style={{ color: '#444' }}>Loading court QRs…</p>
        ) : courtTokens.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: '#444' }}>Add courts to this session to get their scorer QRs.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {courtTokens.map(c => {
              const s = liveOn[c.court_id];
              const qrId = `op-qr-scorer-${c.court_id}`;
              const courtName = c.court_name || 'Court';
              const now = s
                ? `${(s.team_a_players || []).map(p => p.name).join(' / ') || 'Team A'} vs ${(s.team_b_players || []).map(p => p.name).join(' / ') || 'Team B'}`
                : 'No match on court right now';
              return (
                <div key={c.court_id} className="rounded-xl p-4" style={{ background: '#0A0A0A', border: '1px solid #1A1A1A' }}>
                  <p className="text-xs font-bold uppercase mb-1" style={{ color: RED }}>{courtName}</p>
                  <p className="text-xs mb-2" style={{ color: '#737373' }}>Now: {now}</p>
                  <div className="flex flex-col items-center">
                    <div className="p-3 rounded-lg mb-2" style={{ background: '#fff' }}>
                      <QRCodeSVG id={qrId} value={`${origin}/sports/score/court/${c.token}`} size={120} level="H" includeMargin
                        data-label={courtName} data-sublabel="Scan to score this court" />
                    </div>
                    <button onClick={() => downloadQR(qrId, `scorer-${courtName.replace(/\s+/g, '-')}.png`)}
                      data-testid={`op-download-scorer-qr-${c.court_id}`}
                      className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded"
                      style={{ background: '#E6394618', color: RED, border: '1px solid #E6394630' }}>
                      <Download className="h-3 w-3" /> Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}
